import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Button, Spinner } from "reactstrap";

import CustomDataTable from "../../components/SATI/CustomDataTable";
import DateDropdown from "../../components/SATI/DateDropdown";
import AlertModal from "../../components/SATI/AlertModal";
import ErrorBoundary from "../../components/ErrorBoundary";

import { getAdminData } from "../../helpers/satiBack";
import useReportFilter from "../../hooks/useReportFilter";

import "chartist/dist/scss/chartist.scss";

const AdminDashboard = ({ user }) => {
  const [adminData, setAdminData] = useState([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [loadingBtn, setLoadingBtn] = useState(0);

  const getData = useCallback((loading, start, end) => {
    setLoadingBtn(loading);
    setIsDataLoading(true);
    getAdminData(start, end)
      .then((data) => {
        setAdminData(data);
        setLoadingBtn(0);
        setIsDataLoading(false);
      })
      .catch((err) => {
        console.error("AdminDashboard getData error:", err);
        setLoadingBtn(0);
        setIsDataLoading(false);
      });
  }, []);

  const {
    range: dateRange,
    setRange: setDateRange,
    setWeekly,
    setMonthly,
    setCustom
  } = useReportFilter({
    onFilterChange: (id, start, end) => getData(id, start, end)
  });

  useEffect(() => {
    setWeekly();
  }, []);

  const handleWeeklyReport = () => {
    setWeekly();
  };

  const handleMonthlyReport = () => {
    setMonthly();
  };

  const handleCustomRangeReport = () => {
    const start = dateRange[0].clone().date(1);
    const end = dateRange[1].clone().endOf("month");

    if (start.isAfter(end, "month")) {
      setShowAlertModal(true);
      return;
    }

    setCustom([start, end]);
  };

  const tableData = {
    columns: [
      {
        label: "ID",
        field: "EmpresaID",
        sort: "asc",
        width: 50,
      },
      {
        label: "Nombre",
        field: "Nombre",
        sort: "asc",
        width: 220,
      },
      {
        label: "Nivel de estrés",
        field: "StressAvgAVG",
        sort: "asc",
        width: 150,
      },
      {
        label: "Calidad de sueño",
        field: "SleepHoursAVG",
        sort: "asc",
        width: 200,
      },
      {
        label: "Salud cardiovascular (HRV)",
        field: "HRVValueAvgAVG",
        sort: "asc",
        width: 190,
      },
    ],
    rows: adminData.map((d) => {
      try {
        return {
          EmpresaID: d.EmpresaID || "N/A",
          Nombre: d.Nombre || "N/A",
          StressAvgAVG: d.StressAvgAVG || "N/A",
          SleepHoursAVG: d.SleepHoursAVG || "N/A",
          HRVValueAvgAVG: d.HRVValueAvgAVG || "N/A",
          StepsAVG: d.StepsAVG,
          StepsSUM: d.StepsSUM,
          DistanceAVG: d.DistanceAVG,
        };
      } catch (error) {
        console.error(`Error processing row:`, error);
        return {
          EmpresaID: "Error",
          Nombre: "Error",
          StressAvgAVG: "Error",
          SleepHoursAVG: "Error",
          HRVValueAvgAVG: "Error",
        };
      }
    }),
  };

  console.log("Final tableData for MDBDataTable:", tableData);

  return (
    <React.Fragment>
      <div className="container-fluid">
        <Row className="align-items-center">
          <Col>
            <div className="page-title-box py-4">
              <h1 className="text-4xl font-bold font-display text-brand-navy tracking-tight text-center">
                Bienvenido Admin, {user.nombre}
              </h1>
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col className="d-flex justify-content-center align-items-center flex-wrap">
            <Button
              onClick={handleWeeklyReport}
              color="primary"
              className="btn btn-primary waves-effect waves-light mx-1"
            >
              {loadingBtn === 1 && (
                <Spinner size="sm" color="light" className="mr-2" />
              )}
              <span>Reporte semanal</span>
            </Button>
            <Button
              onClick={handleMonthlyReport}
              color="primary"
              className="btn btn-primary waves-effect waves-light mx-1"
            >
              {loadingBtn === 2 && (
                <Spinner size="sm" color="light" className="mr-2" />
              )}
              <span>Reporte mensual</span>
            </Button>
            <div className="d-flex align-items-center m-2">
              <DateDropdown dateRange={dateRange} setDate={setDateRange} />
              <span style={{ margin: "0 4px" }}>–</span>
              <DateDropdown dateRange={dateRange} setDate={setDateRange} end />
              <Button
                onClick={handleCustomRangeReport}
                color="primary"
                className="btn btn-primary waves-effect waves-light mx-1"
              >
                {loadingBtn === 3 && (
                  <Spinner size="sm" color="light" className="mr-2" />
                )}
                <span>Reporte por rango</span>
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            {isDataLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "200px" }}
              >
                <Spinner
                  color="primary"
                  style={{ width: "2rem", height: "2rem" }}
                />
                <span className="ml-3">Cargando datos...</span>
              </div>
            ) : (
              <ErrorBoundary>
                <CustomDataTable tableData={tableData} />
              </ErrorBoundary>
            )}
          </Col>
        </Row>
      </div>
      <AlertModal
        show={showAlertModal}
        toggle={() => setShowAlertModal(!showAlertModal)}
      >
        El rango de meses debería ser válido!
      </AlertModal>
    </React.Fragment>
  );
};

export default AdminDashboard;
